// src/admin/pages/UserDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { obtenerUsuarioPorId, actualizarUsuario, eliminarUsuario } from '../../api';

const UserDetail = () => {
  const { name: userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    obtenerUsuarioPorId(userId).then(data => {
      const [firstName, ...rest] = (data.name || '').split(' ');
      setUser({
        ...data,
        firstName: firstName || '',
        lastName: rest.join(' '),
        segments: ['Regular'],
        newsletter: true
      });
    }).catch(console.error);
  }, [userId]);

  if (!user) return <div className="p-6 text-[rgb(var(--color-text))]">Loading...</div>;

  const handleSave = async () => {
    try {
      const updatedName = `${user.firstName} ${user.lastName}`.trim();
      await actualizarUsuario(user.id, {
        name: updatedName,
        birthday: user.birthday || null
      });
      alert('User updated');
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Update failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await eliminarUsuario(user.id);
      alert('User deleted');
      navigate('/admin/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Delete failed');
    }
  };

  return (
    <div className="p-6 text-[rgb(var(--color-text))] bg-[rgb(var(--color-bg))]">
      <div className="grid grid-cols-4 gap-6">
        <div className="col-span-3 space-y-6 p-6 bg-hover-bg rounded">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-4">
              <h2 className="text-lg font-semibold">Identity</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm">First name *</label>
                  <input
                    type="text"
                    value={user.firstName}
                    onChange={e => setUser({ ...user, firstName: e.target.value })}
                    className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm">Last name *</label>
                  <input
                    type="text"
                    value={user.lastName}
                    onChange={e => setUser({ ...user, lastName: e.target.value })}
                    className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm">Email *</label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded opacity-70 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm">Birthday</label>
                <input
                  type="date"
                  value={user.birthday || ''}
                  onChange={e => setUser({ ...user, birthday: e.target.value })}
                  className="p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                />
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Stats</h2>
              <div>
                <label className="block text-sm">Segments</label>
                <select
                  value={user.segments?.[0] || ''}
                  onChange={e => setUser({ ...user, segments: [e.target.value] })}
                  className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                >
                  {['Regular', 'Collector', 'Reviewer'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm">Has newsletter</label>
                <select
                  value={user.newsletter ? 'Yes' : 'No'}
                  onChange={e => setUser({ ...user, newsletter: e.target.value === 'Yes' })}
                  className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                >
                  <option>Yes</option>
                  <option>No</option>
                </select>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Change Password</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <input
                  type="password"
                  placeholder="Password"
                  className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                />
                <PencilIcon className="absolute right-3 top-2 h-5 w-5 text-[rgb(var(--color-text))]/60" />
              </div>
              <div className="relative">
                <input
                  type="password"
                  placeholder="Confirm password"
                  className="w-full p-2 bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded"
                />
                <PencilIcon className="absolute right-3 top-2 h-5 w-5 text-[rgb(var(--color-text))]/60" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-4 border-t border-[rgb(var(--color-border))]">
            <button onClick={handleSave} className="px-6 py-2 bg-primary text-white rounded disabled:opacity-50 flex items-center">
              <EyeIcon className="hidden" />
              Save
            </button>
            <button onClick={handleDelete} className="px-6 py-2 bg-red-600 text-white rounded flex items-center">
              <TrashIcon className="h-5 w-5 mr-2" />Delete
            </button>
          </div>
        </div>
        <div className="col-span-1 p-4 bg-hover-bg rounded space-y-2">
          <h2 className="text-lg font-semibold">History</h2>
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-5 w-5 text-[rgb(var(--color-text))]/70" />
            <span>First seen: {user.firstSeen}</span>
          </div>
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-5 w-5 text-[rgb(var(--color-text))]/70" />
            <span>Last seen: {user.lastSeen}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
